import { useState, useEffect, useCallback } from 'react';
import find from 'lodash/find';
import filter from 'lodash/filter';
import { getOrderItems } from '../../../utils/order';
import { useIdentity } from '../../../hooks';
import { trackEvent } from '../../../utils/analytics';

interface OrderItem {
  item_id?: string;
  ext_product_id?: string;
  division_id?: string;
}

interface ProductLike {
  ext_product_id?: string;
  division_id?: string;
  currency?: string;
  currency_id?: string;
  [key: string]: unknown;
}

interface UseProductActionsOptions {
  order?: Record<string, unknown> | null;
  fullOrder?: Record<string, unknown> | null;
  onAddItem: (item: Record<string, unknown>) => Promise<OrderItem | void>;
  onDeleteItem: (itemId: string) => void;
  isProductsPage?: boolean;
  /** Global in-flight loading ids (e.g. Redux add_item), used as a fallback for items with no division_id */
  loading?: string[];
  /** Event name prefix for analytics - e.g. 'connected_plus' or 'llm_suggestion' */
  eventPrefix?: string;
  /** Optional callback when an Algolia-style sendEvent should fire */
  onSendConversionEvent?: (item: Record<string, unknown>, label: string) => void;
}

export function useOrderItems(fullOrder: Record<string, unknown> | null | undefined) {
  const [items, setItems] = useState<OrderItem[]>(
    fullOrder ? getOrderItems(fullOrder as any) : []
  );

  useEffect(() => {
    if ((fullOrder as any)?.loaded) {
      setItems(getOrderItems(fullOrder as any));
    }
  }, [fullOrder]);

  return [items, setItems] as const;
}

function getItemKey(item: ProductLike): string {
  return `${item.ext_product_id}::${item.division_id ?? ''}`;
}

export function findSelected(addedItems: OrderItem[], item: ProductLike): OrderItem | undefined {
  return find(addedItems, (addedItem) => {
    if (addedItem.ext_product_id !== item.ext_product_id) {
      return false;
    }

    if (!item.division_id) {
      return true;
    }

    return addedItem.division_id === item.division_id;
  });
}

export function useProductActions({
  order,
  fullOrder,
  onAddItem,
  onDeleteItem,
  isProductsPage = false,
  loading = [],
  eventPrefix = 'connected_plus',
  onSendConversionEvent,
}: UseProductActionsOptions) {
  const [addedItems, setAddedItems] = useOrderItems(isProductsPage ? null : fullOrder);
  const [loadingItems, setLoadingItems] = useState<string[]>([]);
  const identity = useIdentity();

  const isSelected = useCallback((item: ProductLike): boolean => {
    if (isProductsPage) return false;
    return !!findSelected(addedItems, item);
  }, [addedItems, isProductsPage]);

  const isItemLoading = useCallback((item: ProductLike): boolean => {
    return loadingItems.includes(getItemKey(item))
      || (!item.division_id && !!item.ext_product_id && loading.includes(item.ext_product_id));
  }, [loadingItems, loading]);

  const trackAddEvent = useCallback((isDetailPopupModal: boolean) => {
    const event_name = isDetailPopupModal
      ? `add_${eventPrefix}_product_detail_popup`
      : `add_${eventPrefix}_product`;
    trackEvent(event_name, {
      tenant_id: (identity as any).tenant_id,
      login_email: (identity as any).login_email,
    });
  }, [identity, eventPrefix]);

  const trackClickEvent = useCallback(() => {
    const event_name = `click_${eventPrefix}_product`;
    trackEvent(event_name, {
      tenant_id: (identity as any).tenant_id,
      login_email: (identity as any).login_email,
    });
  }, [identity, eventPrefix]);

  const handleClickProductAction = useCallback(async (
    item: ProductLike,
    isDetailPopupModal = false,
  ) => {
    if (isProductsPage) return;

    const added = findSelected(addedItems, item);
    const loadingKey = getItemKey(item);
    if (!added) {
      setLoadingItems((prev) => (prev.includes(loadingKey) ? prev : [...prev, loadingKey]));
      try {
        const addedItem = await onAddItem({
          ...item,
          currency_id: (item.currency ?? item.currency_id ?? 'USD') as string,
        });

        if (!order) {
          setAddedItems([...addedItems, {
            ext_product_id: item.ext_product_id,
            division_id: item.division_id,
          }]);
          trackAddEvent(isDetailPopupModal);
        } else if (addedItem) {
          if (onSendConversionEvent) {
            onSendConversionEvent(item, `Product Added to ${(order as any).order_type}`);
          }
          setAddedItems([...addedItems, addedItem as OrderItem]);
          trackAddEvent(isDetailPopupModal);
        }
      } finally {
        setLoadingItems((prev) => prev.filter((key) => key !== loadingKey));
      }
    } else {
      if (order && added.item_id) {
        onDeleteItem(added.item_id);
        setAddedItems(filter(addedItems, (existing) => existing.item_id !== added.item_id));
      } else {
        setAddedItems(filter(addedItems, (existing) => {
          return existing.ext_product_id !== item.ext_product_id || existing.division_id !== added.division_id;
        }));
      }
    }
  }, [addedItems, setAddedItems, order, onAddItem, onDeleteItem, isProductsPage, trackAddEvent, onSendConversionEvent]);

  return {
    addedItems,
    isSelected,
    isItemLoading,
    handleClickProductAction,
    trackClickEvent,
  };
}
